// Module-level session store — the React equivalent of the Svelte
// `writable` that apps/web-svelte uses. Components subscribe via
// `useSession` (built on useSyncExternalStore); non-component modules
// (the API client, the auth actions) call `currentSession`,
// `setSession`, `clearAuth` directly.
//
// Single source of truth: localStorage hydrates on import-time, every
// login/logout/refresh writes through here. The API client reads from
// this snapshot, not from localStorage, so a logout in one place is
// observed everywhere without an extra read.

import {
  clearSession as clearStored,
  loadSession,
  saveSession as saveStored,
  type Session,
} from "./session";
import { identifyTelemetry, resetTelemetry } from "../telemetry/posthog";

type Listener = () => void;
const listeners = new Set<Listener>();
let snapshot: Session | null = loadSession();
// Boot-restore: a session hydrated from localStorage never passes
// through setSession(), so without this the replay stays anonymous
// until the next login. identifyTelemetry() parks the identity until
// the SDK is up (initTelemetry() runs after this module is imported).
if (snapshot) {
  identifyTelemetry(snapshot.user.id, snapshot.organization.id, snapshot.user.email);
}

function notify(): void {
  for (const l of listeners) l();
}

export function currentSession(): Session | null {
  return snapshot;
}

export function setSession(next: Session): void {
  snapshot = next;
  saveStored(next);
  notify();
  identifyTelemetry(next.user.id, next.organization.id, next.user.email);
}

export function clearAuth(): void {
  snapshot = null;
  clearStored();
  notify();
  resetTelemetry();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
